import { useState, useEffect, useRef, useCallback } from "react";
import { buildImportPhoneVariants, normalizeImportPhone } from "@/lib/import-phone";
import { resolveAppointmentConversationId } from "@/lib/appointment-conversation";
import { createPortal } from "react-dom";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { invokeGoogleCalendar } from "@/lib/google-calendar-invoke";
import { GoogleCalendarDialog } from "@/components/integrations/GoogleCalendarDialog";
import {
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Calendar } from "@/components/ui/calendar";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { format, addMinutes, setHours, setMinutes, isBefore, startOfDay } from "date-fns";
import { ptBR } from "date-fns/locale";
import { CalendarIcon, Check, ChevronsUpDown, Loader2, Instagram, Minus, X, Maximize2, Search } from "lucide-react";
import { MessengerIcon } from "@/components/icons/MessengerIcon";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { cn } from "@/lib/utils";
import { prepareLeadSearchTerm } from "@/lib/leads-kanban-utils";
import { isGcalMultiCalendarEnabled } from "@/lib/gcal-feature-flags";
import { useGoogleCalendars } from "@/hooks/useGoogleCalendars";
import { useAuth } from "@/hooks/useAuth";
import { useMemberPermissions } from "@/hooks/useMemberPermissions";
import { useMemberCalendarAccess } from "@/hooks/useMemberCalendarAccess";
import {
  listEnabledSyncCalendars,
  resolveDefaultSyncCalendarId,
} from "@/lib/gcal-calendar-colors";

interface Lead {
  id: string;
  name: string;
  email?: string;
  phone?: string;
  channel?: string | null;
}

interface Product {
  id: string;
  name: string;
  duration?: number;
  type?: string;
}

export interface AppointmentRescheduleContext {
  previousGoogleEventId?: string;
  previousGoogleCalendarId?: string;
  previousAppointmentDate?: string;
  previousAppointmentId?: string;
  previousDuration?: number;
  previousServiceName?: string;
  previousServiceId?: string;
  previousReason?: string;
  previousNotes?: string;
}

interface AppointmentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Controle externo do minimize (layout global). */
  minimized?: boolean;
  onMinimizedChange?: (minimized: boolean) => void;
  workspaceId: string;
  initialDate?: Date;
  initialHour?: number;
  initialLeadId?: string;
  /** Conversa WhatsApp de origem, quando o horário nasce no chat. */
  conversationId?: string | null;
  hasGoogleCalendar?: boolean;
  /**
   * True enquanto o status da conexão Google Calendar ainda não foi resolvido.
   * Bloqueia o botão "Criar" para impedir submit antes de saber se devemos sincronizar.
   */
  isCheckingCalendarStatus?: boolean;
  onSuccess: () => void;
  rescheduleContext?: AppointmentRescheduleContext;
}

const TIME_SLOTS = Array.from({ length: 28 }, (_, i) => {
  const hour = Math.floor(i / 2) + 8; // Start at 8:00
  const minute = (i % 2) * 30;
  return {
    value: `${hour}:${minute === 0 ? "00" : "30"}`,
    label: `${hour.toString().padStart(2, "0")}:${minute === 0 ? "00" : "30"}`,
  };
});

const DURATION_OPTIONS = [
  { value: 30, label: "30 minutos" },
  { value: 45, label: "45 minutos" },
  { value: 60, label: "1 hora" },
  { value: 90, label: "1h 30min" },
  { value: 120, label: "2 horas" },
];

export function AppointmentDialog({
  open,
  onOpenChange,
  minimized: minimizedProp,
  onMinimizedChange,
  workspaceId,
  initialDate,
  initialHour,
  initialLeadId,
  conversationId,
  hasGoogleCalendar = false,
  isCheckingCalendarStatus = false,
  onSuccess,
  rescheduleContext,
}: AppointmentDialogProps) {
  const { toast } = useToast();
  const { user } = useAuth();
  const { memberId, canSeeAllCalendars, role: memberRole, permissions: memberPermissions } = useMemberPermissions();
  const [assignSelf, setAssignSelf] = useState(true);
  const showAssignSelf = Boolean(memberId) && memberRole !== "owner" && memberRole !== "admin";
  const assignSelfLocked = memberPermissions?.conversations_visibility === "assigned_only";
  const { allowedGoogleCalendarIds } = useMemberCalendarAccess({
    workspaceId,
    memberId,
    canSeeAllCalendars,
  });
  const { connections: gcalConnections } = useGoogleCalendars();
  const gcalMultiEnabled = isGcalMultiCalendarEnabled();
  const visibleSyncCalendars = listEnabledSyncCalendars(
    gcalConnections,
    allowedGoogleCalendarIds,
  );
  const [loading, setLoading] = useState(false);
  const [leads, setLeads] = useState<Lead[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [leadsLoading, setLeadsLoading] = useState(false);

  // Form state
  const [selectedLeadId, setSelectedLeadId] = useState<string>("");
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(initialDate || new Date());
  const [selectedTime, setSelectedTime] = useState<string>(
    initialHour !== undefined ? `${initialHour}:00` : "09:00"
  );
  const [duration, setDuration] = useState<number>(60);
  const [selectedServiceId, setSelectedServiceId] = useState<string>("");
  const [notes, setNotes] = useState<string>("");
  const [appointmentReason, setAppointmentReason] = useState<string>("");
  const [syncGoogleCalendar, setSyncGoogleCalendar] = useState<boolean>(hasGoogleCalendar);
  const [selectedSyncCalendarId, setSelectedSyncCalendarId] = useState<string>("");

  // Lead mode: select existing, create new, or standalone (no lead)
  const [leadMode, setLeadMode] = useState<"select" | "create" | "standalone">("select");

  // Standalone fields
  const [standaloneSummary, setStandaloneSummary] = useState<string>("");
  const [standaloneAttendeeName, setStandaloneAttendeeName] = useState<string>("");
  const [standaloneAttendeeEmail, setStandaloneAttendeeEmail] = useState<string>("");

  // Lead editable fields
  const [leadName, setLeadName] = useState<string>("");
  const [leadEmail, setLeadEmail] = useState<string>("");
  const [leadPhone, setLeadPhone] = useState<string>("");

  // Lead combobox state
  const [leadSearchOpen, setLeadSearchOpen] = useState(false);
  const [leadSearchQuery, setLeadSearchQuery] = useState("");

  // Double booking alert
  const [doubleBookingOpen, setDoubleBookingOpen] = useState(false);
  const [existingAppointmentDate, setExistingAppointmentDate] = useState<string | null>(null);

  // Anti-double-click + idempotency for Google Calendar
  const submittingRef = useRef(false);
  const idempotencyKeyRef = useRef<string | null>(null);

  // Reconnect dialog (opened on GOOGLE_AUTH_NEEDS_RECONNECT)
  const [showReconnect, setShowReconnect] = useState(false);

  // Minimize — pode ser controlado pelo AppointmentDraftHost (persiste entre rotas)
  const [minimizedInternal, setMinimizedInternal] = useState(false);
  const minimized = minimizedProp ?? minimizedInternal;
  const setMinimized = onMinimizedChange ?? setMinimizedInternal;

  const isRescheduling = !!rescheduleContext;

  useEffect(() => {
    if (open && workspaceId) {
      fetchProducts();
    }
  }, [open, workspaceId]);

  useEffect(() => {
    if (initialDate) {
      setSelectedDate(initialDate);
    }
    if (initialHour !== undefined) {
      setSelectedTime(`${initialHour}:00`);
    }
    if (initialLeadId) {
      setSelectedLeadId(initialLeadId);
    }
  }, [initialDate, initialHour, initialLeadId]);

  // Pre-fill from reschedule context when opening
  useEffect(() => {
    if (open && rescheduleContext) {
      if (rescheduleContext.previousDuration) {
        setDuration(rescheduleContext.previousDuration);
      }
      if (rescheduleContext.previousServiceId) {
        setSelectedServiceId(rescheduleContext.previousServiceId);
      }
      if (rescheduleContext.previousReason) {
        setAppointmentReason(rescheduleContext.previousReason);
      }
      if (rescheduleContext.previousNotes) {
        setNotes(rescheduleContext.previousNotes);
      }
      // Pre-fill time with minutes from previous appointment
      if (rescheduleContext.previousAppointmentDate) {
        const prevDate = new Date(rescheduleContext.previousAppointmentDate);
        const h = prevDate.getHours();
        const m = prevDate.getMinutes();
        setSelectedTime(`${h}:${m === 0 ? "00" : m.toString().padStart(2, "0")}`);
      }
    }
  }, [open, rescheduleContext]);

  // Populate editable fields when lead is selected (only in select mode)
  useEffect(() => {
    if (leadMode === "select") {
      const lead = leads.find((l) => l.id === selectedLeadId);
      if (lead) {
        setLeadName(lead.name || "");
        setLeadEmail(lead.email || "");
        setLeadPhone(lead.phone || "");
        setNotes((lead as any).notes || "");
      } else if (!selectedLeadId) {
        setLeadName("");
        setLeadEmail("");
        setLeadPhone("");
      }
    }
  }, [selectedLeadId, leads, leadMode]);

  // Reset form when dialog opens (host remonta com key ao abrir nova sessão)
  useEffect(() => {
    if (open) {
      setLeadMode("select");
      setStandaloneSummary("");
      setStandaloneAttendeeName("");
      setStandaloneAttendeeEmail("");
      if (!onMinimizedChange) setMinimized(false);
      idempotencyKeyRef.current = null;
    }
  }, [open, onMinimizedChange, setMinimized]);

  // Keep `syncGoogleCalendar` in sync with the parent prop.
  useEffect(() => {
    if (open) setSyncGoogleCalendar(hasGoogleCalendar);
  }, [open, hasGoogleCalendar]);

  // Default agenda quando multi-agenda está ativo
  useEffect(() => {
    if (!open || visibleSyncCalendars.length === 0) return;

    if (rescheduleContext?.previousGoogleCalendarId) {
      const fromPrevious = visibleSyncCalendars.find(
        (c) => c.calendar_id === rescheduleContext.previousGoogleCalendarId,
      );
      if (fromPrevious) {
        setSelectedSyncCalendarId(fromPrevious.id);
        return;
      }
    }

    setSelectedSyncCalendarId((prev) => {
      if (prev && visibleSyncCalendars.some((c) => c.id === prev)) return prev;
      return resolveDefaultSyncCalendarId(visibleSyncCalendars) ?? "";
    });
  }, [open, visibleSyncCalendars, rescheduleContext?.previousGoogleCalendarId]);

  // ── Server-side lead search ──────────────────────────────────────────────
  // Workspaces can have thousands of leads; preloading the first 1000 made
  // most leads unsearchable (e.g. "carlos carlosdias05" wasn't in the first
  // page). We now query Postgres directly for each search term, with debounce.
  const leadSearchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const mapLeadsWithChannel = useCallback(
    async (rows: { id: string; name: string; email?: string | null; phone?: string | null }[]) => {
      if (rows.length === 0) return [] as Lead[];
      const ids = rows.map((l) => l.id);
      const { data: convs } = await supabase
        .from("conversations")
        .select("lead_id, channel")
        .in("lead_id", ids);
      const channelByLead = new Map<string, string>();
      for (const c of convs || []) {
        if (c.lead_id && !channelByLead.has(c.lead_id)) {
          channelByLead.set(c.lead_id, c.channel);
        }
      }
      return rows.map((l) => ({
        id: l.id,
        name: l.name,
        email: l.email ?? undefined,
        phone: l.phone ?? undefined,
        channel: channelByLead.get(l.id) ?? null,
      }));
    },
    [],
  );

  const searchLeads = useCallback(
    async (term: string) => {
      if (!workspaceId) return;
      setLeadsLoading(true);
      try {
        const searchTerm = prepareLeadSearchTerm(term);
        if (searchTerm.length > 0) {
          const { data, error } = await supabase.rpc("search_leads_by_term", {
            p_workspace_id: workspaceId,
            p_term: searchTerm,
            p_limit: 30,
          });
          if (error) throw error;
          setLeads(await mapLeadsWithChannel((data || []) as { id: string; name: string; email?: string | null; phone?: string | null }[]));
          return;
        }
        const { data, error } = await supabase
          .from("leads")
          .select("id, name, email, phone")
          .eq("workspace_id", workspaceId)
          .order("created_at", { ascending: false })
          .limit(30);
        if (error) throw error;
        setLeads(await mapLeadsWithChannel(data || []));
      } catch (error) {
        console.error("Error searching leads:", error);
      } finally {
        setLeadsLoading(false);
      }
    },
    [workspaceId, mapLeadsWithChannel],
  );

  // Trigger search when the lead picker opens (preload recent) and on each
  // keystroke with a 300ms debounce.
  useEffect(() => {
    if (!open || !workspaceId) return;
    if (leadMode !== "select") return;
    if (leadSearchDebounceRef.current) clearTimeout(leadSearchDebounceRef.current);
    leadSearchDebounceRef.current = setTimeout(() => {
      searchLeads(leadSearchQuery);
    }, leadSearchQuery.trim().length === 0 ? 0 : 300);
    return () => {
      if (leadSearchDebounceRef.current) clearTimeout(leadSearchDebounceRef.current);
    };
  }, [open, workspaceId, leadMode, leadSearchQuery, searchLeads]);

  // When the dialog is opened with a preselected lead, fetch it explicitly so
  // its name shows up in the trigger even before the user searches.
  useEffect(() => {
    if (!open || !workspaceId || !initialLeadId) return;
    if (leads.some((l) => l.id === initialLeadId)) return;
    (async () => {
      const { data } = await supabase
        .from("leads")
        .select("id, name, email, phone, notes, conversations(channel)")
        .eq("id", initialLeadId)
        .eq("workspace_id", workspaceId)
        .maybeSingle();
      if (data) {
        setLeads((prev) => {
          if (prev.some((l) => l.id === data.id)) return prev;
          return [
            ...prev,
            {
              id: data.id,
              name: data.name,
              email: data.email ?? undefined,
              phone: data.phone ?? undefined,
              channel: (data as any).conversations?.[0]?.channel ?? null,
            },
          ];
        });
      }
    })();
  }, [open, workspaceId, initialLeadId]);

  const fetchProducts = async () => {
    try {
      const { data, error } = await supabase
        .from("products")
        .select("id, name, duration, type")
        .eq("workspace_id", workspaceId)
        .eq("is_active", true)
        .order("name");

      if (error) throw error;
      setProducts(data || []);
    } catch (error) {
      console.error("Error fetching products:", error);
    }
  };


  // Auto-fill duration when a product with duration is selected
  useEffect(() => {
    if (selectedServiceId) {
      const product = products.find((p) => p.id === selectedServiceId);
      if (product?.duration) {
        setDuration(product.duration);
      }
    }
  }, [selectedServiceId, products]);

  const handleSubmit = async () => {
    // ── ANTI-DUPLO-CLIQUE GUARD ──
    if (submittingRef.current) {
      console.log("[AppointmentDialog] submit ignored — already in progress");
      return;
    }

    // Validate based on mode
    if (leadMode === "standalone") {
      if (!standaloneSummary.trim()) {
        toast({
          title: "Título obrigatório",
          description: "Informe um título para o compromisso avulso.",
          variant: "destructive",
        });
        return;
      }
    } else if (leadMode === "create") {
      if (!leadName.trim()) {
        toast({
          title: "Nome obrigatório",
          description: "Informe o nome do cliente para criar o lead.",
          variant: "destructive",
        });
        return;
      }
    } else {
      if (!selectedLeadId) {
        toast({
          title: "Lead obrigatório",
          description: "Selecione um lead existente ou crie um novo.",
          variant: "destructive",
        });
        return;
      }
    }

    if (!selectedServiceId && !appointmentReason.trim()) {
      toast({
        title: "Motivo obrigatório",
        description: "Selecione um produto/serviço ou informe o motivo do agendamento.",
        variant: "destructive",
      });
      return;
    }

    if (!selectedDate) {
      toast({
        title: "Data obrigatória",
        description: "Selecione uma data para o agendamento.",
        variant: "destructive",
      });
      return;
    }

    if (
      syncGoogleCalendar &&
      hasGoogleCalendar &&
      visibleSyncCalendars.length > 1 &&
      !selectedSyncCalendarId
    ) {
      toast({
        title: "Agenda obrigatória",
        description: "Selecione em qual agenda Google criar o evento.",
        variant: "destructive",
      });
      return;
    }

    const service = products.find((s) => s.id === selectedServiceId);

    submittingRef.current = true;
    setLoading(true);

    // Preserve idempotency_key across "Agendar mesmo assim" confirmations
    if (!idempotencyKeyRef.current) {
      idempotencyKeyRef.current = (typeof crypto !== "undefined" && "randomUUID" in crypto)
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    }

    try {
      // Parse time
      const [hour, minute] = selectedTime.split(":").map(Number);
      const appointmentDate = setMinutes(setHours(selectedDate, hour), minute);

      // Validate not in past
      if (isBefore(appointmentDate, new Date())) {
        toast({
          title: "Data inválida",
          description: "Não é possível agendar para uma data/horário no passado.",
          variant: "destructive",
        });
        return;
      }

      // ── Pré-flight list_calendars REMOVIDO ──
      // create_event devolve 401 GOOGLE_AUTH_NEEDS_RECONNECT diretamente quando preciso.

      // ── DOUBLE-BOOKING CHECK (lead-based modes) ──
      const targetLeadId = leadMode === "select" ? selectedLeadId : null;
      if (leadMode !== "standalone" && targetLeadId && !doubleBookingOpen) {
        const { data: existingFuture } = await supabase
          .from("appointments")
          .select("id, scheduled_date, google_event_id, google_calendar_id, last_synced_at")
          .eq("lead_id", targetLeadId)
          .eq("workspace_id", workspaceId)
          .eq("status", "scheduled")
          .gt("scheduled_date", new Date().toISOString())
          .limit(5);

        const otherAppointments = (existingFuture || []).filter(
          (a) => !rescheduleContext?.previousAppointmentId || a.id !== rescheduleContext.previousAppointmentId
        );

        if (otherAppointments.length > 0) {
          const existing: any = otherAppointments[0];
          const lastSyncedMs = existing.last_synced_at ? new Date(existing.last_synced_at).getTime() : 0;
          const isFresh = lastSyncedMs > 0 && Date.now() - lastSyncedMs < 30 * 60 * 1000;

          // Trust local DB if fresh OR no Google sync OR no google_event_id
          if (isFresh || !syncGoogleCalendar || !hasGoogleCalendar || !existing.google_event_id) {
            setExistingAppointmentDate(existing.scheduled_date);
            setDoubleBookingOpen(true);
            return;
          }

          // Stale + has google_event_id: verify on Google before alerting
          const r = await invokeGoogleCalendar<any>("get_event", workspaceId, {
            eventId: existing.google_event_id,
          });

          if (!r.ok) {
            if (r.kind === "needs_reconnect") {
              setShowReconnect(true);
              return;
            }
            // transient/fatal: NÃO cancelar nada, confiar no DB e alertar
            setExistingAppointmentDate(existing.scheduled_date);
            setDoubleBookingOpen(true);
            return;
          }

          const data: any = r.data;
          if (data?.deleted || !data?.event) {
            // Event was deleted from Google — mark local as cancelled_external
            await supabase
              .from("appointments")
              .update({
                status: "cancelled",
                cancelled_at: new Date().toISOString(),
                sync_status: "cancelled_external",
              })
              .eq("id", existing.id)
              .eq("workspace_id", workspaceId);
            // Continue — old appointment was orphaned
          } else {
            setExistingAppointmentDate(existing.scheduled_date);
            setDoubleBookingOpen(true);
            return;
          }
        }
      }

      let eventId: string | null = null;
      let calendarId: string | null = null;
      let iCalUID: string | null = null;
      let leadId: string | null = selectedLeadId || null;

      // Standalone mode: no lead
      if (leadMode === "standalone") {
        leadId = null;
      } else if (leadMode === "create") {
        // Reaproveita contato existente com o mesmo telefone (com/sem 55 ou 9º dígito)
        const normalizedPhone = normalizeImportPhone(leadPhone);
        const variants = buildImportPhoneVariants(leadPhone);
        let existingId: string | null = null;
        if (variants.length > 0) {
          const { data: existing } = await supabase
            .from("leads")
            .select("id")
            .eq("workspace_id", workspaceId)
            .in("phone", variants)
            .limit(1)
            .maybeSingle();
          existingId = existing?.id ?? null;
        }

        if (existingId) {
          leadId = existingId;
        } else {
          const { data: newLead, error: createError } = await supabase
            .from("leads")
            .insert({
              name: leadName.trim(),
              email: leadEmail || null,
              phone: normalizedPhone || null,
              workspace_id: workspaceId,
              stage: "novo",
              ...(memberId && showAssignSelf && (assignSelfLocked || assignSelf)
                ? { assigned_member_id: memberId }
                : {}),
            })
            .select("id")
            .single();

          if (createError) {
            console.error("create lead failed", createError);
            toast({
              title: "Erro ao criar lead",
              description: "Não foi possível salvar o contato. Tente novamente.",
              variant: "destructive",
            });
            return;
          }

          leadId = newLead.id;
        }
      }

      // ── GOOGLE CALENDAR SYNC (via wrapper with retry/classification) ──
      if (syncGoogleCalendar && hasGoogleCalendar) {
        const endTime = addMinutes(appointmentDate, duration);

        const result = rescheduleContext?.previousGoogleEventId
          ? await invokeGoogleCalendar<any>("reschedule_event", workspaceId, {
              eventId: rescheduleContext.previousGoogleEventId,
              attendeeEmail: leadEmail || "",
              newStartTime: appointmentDate.toISOString(),
              newEndTime: endTime.toISOString(),
              allowOverlapping: true,
              ...(selectedSyncCalendarId
                ? { syncCalendarId: selectedSyncCalendarId }
                : {}),
            })
          : await invokeGoogleCalendar<any>("create_event", workspaceId, {
              source: "manual",
              allowOverlapping: true,
              startTime: appointmentDate.toISOString(),
              endTime: endTime.toISOString(),
              attendeeName:
                leadMode === "standalone"
                  ? standaloneAttendeeName || standaloneSummary
                  : leadName,
              attendeeEmail:
                leadMode === "standalone" ? standaloneAttendeeEmail : leadEmail,
              customerPhone: leadMode === "standalone" ? "" : leadPhone || "",
              serviceItem:
                service?.name ||
                appointmentReason.trim() ||
                (leadMode === "standalone" ? standaloneSummary : ""),
              description: notes || "",
              leadId,
              idempotency_key: idempotencyKeyRef.current,
              ...(selectedSyncCalendarId
                ? { syncCalendarId: selectedSyncCalendarId }
                : {}),
            });

        if (!result.ok) {
          if (result.kind === "needs_reconnect") {
            setShowReconnect(true);
            return;
          }
          if (result.kind === "slot_busy") {
            toast({
              title: "Horário ocupado no Google Calendar",
              description: result.message,
              variant: "destructive",
            });
            return;
          }
          if (result.kind === "ambiguous_match") {
            toast({
              title: "Eventos similares no Google Calendar",
              description: result.message,
              variant: "destructive",
            });
            return;
          }
          // transient (após retries) ou fatal: NÃO inserir local
          toast({
            title: "Falha ao sincronizar com Google Calendar",
            description: `${result.message} Tente novamente em alguns segundos.`,
            variant: "destructive",
          });
          return;
        }

        // Extract identifiers (create_event vs reschedule_event return shape)
        const data = result.data || {};
        eventId =
          data.eventId ??
          data.rescheduledEvent?.id ??
          rescheduleContext?.previousGoogleEventId ??
          null;
        calendarId =
          data.calendarId ??
          data.rescheduledEvent?.calendarId ??
          rescheduleContext?.previousGoogleCalendarId ??
          (selectedSyncCalendarId || null);
        iCalUID = data.iCalUID ?? data.rescheduledEvent?.iCalUID ?? null;

        // Constraint guard: NEVER insert with eventId but no calendarId
        if (eventId && !calendarId) {
          console.error(
            "[AppointmentDialog] Google response returned eventId without calendarId — aborting insert.",
            { data, selectedSyncCalendarId, rescheduleContext },
          );
          toast({
            title: "Resposta inválida do Google Calendar",
            description: "Tente salvar novamente.",
            variant: "destructive",
          });
          return;
        }

        // Sync foi solicitado mas resposta veio sem identificadores → não silencia
        if (!eventId) {
          console.error(
            "[AppointmentDialog] Google sync requested but response had no eventId — aborting local insert.",
            data,
          );
          toast({
            title: "Falha ao sincronizar com Google Calendar",
            description: "Tente novamente em alguns segundos.",
            variant: "destructive",
          });
          return;
        }
      }

      // If rescheduling, cancel old appointment record by ID
      if (rescheduleContext?.previousAppointmentId) {
        await supabase
          .from("appointments")
          .update({
            status: "cancelled",
            cancelled_at: new Date().toISOString(),
            cancellation_type: "rescheduled",
          } as any)
          .eq("id", rescheduleContext.previousAppointmentId)
          .eq("workspace_id", workspaceId);

        if (leadId) {
          await supabase
            .from("appointment_reminders")
            .update({ status: "cancelled" })
            .eq("lead_id", leadId)
            .eq("workspace_id", workspaceId)
            .eq("appointment_date", rescheduleContext.previousAppointmentDate!)
            .eq("status", "pending");
        }
      } else if (rescheduleContext?.previousAppointmentDate && leadId) {
        await supabase
          .from("appointments")
          .update({ status: "cancelled", cancelled_at: new Date().toISOString() })
          .eq("lead_id", leadId)
          .eq("workspace_id", workspaceId)
          .eq("scheduled_date", rescheduleContext.previousAppointmentDate)
          .eq("status", "scheduled");
      }

      // Update lead with appointment fields (only if has lead)
      if (leadId) {
        const existingLead = leads.find((l) => l.id === leadId);
        const existingMetadata = (existingLead as any)?.metadata || {};
        const newMetadata =
          selectedServiceId && service
            ? {
                ...existingMetadata,
                last_service_id: selectedServiceId,
                last_service_name: service.name,
              }
            : {
                ...existingMetadata,
                appointment_reason: appointmentReason.trim() || undefined,
              };

        await supabase
          .from("appointment_reminders")
          .update({ status: "cancelled" })
          .eq("lead_id", leadId)
          .eq("workspace_id", workspaceId)
          .eq("status", "pending");

        const { error: updateError } = await supabase
          .from("leads")
          .update({
            name: leadName,
            email: leadEmail || null,
            phone: leadPhone || null,
            current_appointment_date: appointmentDate.toISOString(),
            current_appointment_id: eventId,
            current_appointment_duration: duration,
            notes: notes || null,
            metadata: Object.keys(newMetadata).length > 0 ? newMetadata : null,
          })
          .eq("id", leadId)
          .eq("workspace_id", workspaceId);

        if (updateError) throw updateError;
      }

      const appointmentConversationId = leadId
        ? await resolveAppointmentConversationId({
            workspaceId,
            leadId,
            phone: leadPhone || null,
            explicitConversationId: conversationId,
            previousAppointmentId: rescheduleContext?.previousAppointmentId,
          })
        : null;

      // ── INSERT INTO appointments TABLE (constraint-safe) ──
      const insertPayload: any = {
        lead_id: leadId,
        workspace_id: workspaceId,
        scheduled_date: appointmentDate.toISOString(),
        duration_minutes: duration,
        google_event_id: eventId,
        google_calendar_id: eventId ? calendarId : null,
        google_ical_uid: iCalUID,
        service_name: service?.name || null,
        reason: appointmentReason.trim() || null,
        notes: notes || null,
        status: "scheduled",
        origin_source: "manual",
        sync_status: eventId ? "synced" : "local_only",
        last_synced_at: eventId ? new Date().toISOString() : null,
        summary: leadMode === "standalone" ? standaloneSummary.trim() : null,
        attendee_name: leadMode === "standalone" ? standaloneAttendeeName || null : null,
        attendee_email: leadMode === "standalone" ? standaloneAttendeeEmail || null : null,
        metadata: appointmentConversationId
          ? { source: "appointment_dialog", conversation_id: appointmentConversationId }
          : { source: "appointment_dialog" },
      };

      const { error: insertErr } = await supabase.from("appointments").insert(insertPayload);

      if (insertErr) {
        // 23505 = unique violation from idx_appointments_google_event_unique
        // Means another concurrent submit already saved the same event — treat as success.
        if ((insertErr as any).code === "23505") {
          console.log(
            "[AppointmentDialog] duplicate insert detected by unique index — already saved by previous click.",
          );
        } else {
          throw insertErr;
        }
      }

      const displayName = leadMode === "standalone" ? standaloneSummary : leadName;

      toast({
        title: isRescheduling ? "Agendamento reagendado" : "Agendamento criado",
        description: `Agendamento para ${displayName} em ${format(
          appointmentDate,
          "dd/MM/yyyy 'às' HH:mm",
          { locale: ptBR },
        )}.`,
      });

      // Success → clear idempotency_key
      idempotencyKeyRef.current = null;

      // Reset form
      setLeadMode("select");
      setSelectedLeadId("");
      setSelectedDate(new Date());
      setSelectedTime("09:00");
      setDuration(60);
      setSelectedServiceId("");
      setNotes("");
      setAppointmentReason("");
      setLeadName("");
      setLeadEmail("");
      setLeadPhone("");
      setStandaloneSummary("");
      setStandaloneAttendeeName("");
      setStandaloneAttendeeEmail("");
      setSyncGoogleCalendar(hasGoogleCalendar);

      onOpenChange(false);
      onSuccess();
    } catch (error) {
      console.error("Error creating appointment:", error);
      toast({
        title: "Erro ao criar agendamento",
        description: "Não foi possível criar o agendamento.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
      submittingRef.current = false;
    }
  };

  const selectedLead = leads.find((l) => l.id === selectedLeadId);

  return (
    <>
    <DialogPrimitive.Root
      open={open}
      modal={!minimized}
      onOpenChange={(o) => {
        // Enquanto minimizado, ignorar tentativas de fechar (clique fora / Esc).
        if (!o && minimized) return;
        if (!o) setMinimized(false);
        onOpenChange(o);
      }}
    >
      <DialogPrimitive.Portal>
        {!minimized && (
          <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        )}
        <DialogPrimitive.Content
          aria-describedby={undefined}
          className={cn(
            "fixed left-[50%] top-[50%] z-50 grid w-full max-w-[500px] translate-x-[-50%] translate-y-[-50%] gap-4 border bg-background p-6 shadow-lg sm:rounded-lg max-h-[90vh] overflow-y-auto",
            "shadow-[0_8px_24px_-4px_hsl(240_10%_15%_/_0.18)] dark:shadow-lg",
            "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
            minimized && "hidden pointer-events-none"
          )}
        >
        <div className="flex items-center justify-between gap-2">
          <DialogPrimitive.Title className="text-lg font-semibold leading-none tracking-tight flex items-center gap-2">
            <CalendarIcon className="w-5 h-5 text-primary" />
            {isRescheduling ? "Reagendar Agendamento" : "Novo Agendamento"}
          </DialogPrimitive.Title>
          <div className="flex items-center gap-1 shrink-0">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() => setMinimized(true)}
              title="Minimizar"
              aria-label="Minimizar"
            >
              <Minus className="h-4 w-4" />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() => onOpenChange(false)}
              title="Fechar"
              aria-label="Fechar"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>


        <div className="space-y-4 py-4">
          {/* Lead Mode Toggle — hidden when rescheduling (lead is locked) */}
          {!isRescheduling && (
          <div className="space-y-2">
            <Label>Tipo</Label>
            <div className="flex gap-2">
              <Button
                type="button"
                variant={leadMode === "select" ? "default" : "outline"}
                size="sm"
                className="flex-1"
                onClick={() => {
                  setLeadMode("select");
                  setLeadName("");
                  setLeadEmail("");
                  setLeadPhone("");
                  setStandaloneSummary("");
                }}
              >
                Lead existente
              </Button>
              <Button
                type="button"
                variant={leadMode === "create" ? "default" : "outline"}
                size="sm"
                className="flex-1"
                onClick={() => {
                  setLeadMode("create");
                  setSelectedLeadId("");
                  setLeadName("");
                  setLeadEmail("");
                  setLeadPhone("");
                }}
              >
                Criar novo lead
              </Button>
              <Button
                type="button"
                variant={leadMode === "standalone" ? "default" : "outline"}
                size="sm"
                className="flex-1"
                onClick={() => {
                  setLeadMode("standalone");
                  setSelectedLeadId("");
                  setLeadName("");
                  setLeadEmail("");
                  setLeadPhone("");
                }}
              >
                Compromisso avulso
              </Button>
            </div>
          </div>
          )}

          {/* Lead Selection (only in select mode, hidden when rescheduling) */}
          {leadMode === "select" && !isRescheduling && (
            <div className="space-y-2">
              <Popover open={leadSearchOpen} onOpenChange={setLeadSearchOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={leadSearchOpen}
                    className="w-full justify-between"
                  >
                    {selectedLead ? selectedLead.name : "Selecionar lead..."}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-full p-0" align="start">
                  <Command shouldFilter={false}>
                    <CommandInput
                      placeholder="Buscar lead por nome, telefone ou email..."
                      value={leadSearchQuery}
                      onValueChange={setLeadSearchQuery}
                    />
                    <CommandList>
                      <CommandEmpty>
                        {leadsLoading ? "Carregando..." : "Nenhum lead encontrado."}
                      </CommandEmpty>
                      <CommandGroup>
                        {leads.map((lead) => (
                          <CommandItem
                            key={lead.id}
                            value={lead.id}
                            onSelect={(val) => {
                              setSelectedLeadId(val);
                              setLeadSearchOpen(false);
                              setLeadSearchQuery("");
                            }}
                          >

                            <Check
                              className={cn(
                                "mr-2 h-4 w-4 shrink-0",
                                selectedLeadId === lead.id ? "opacity-100" : "opacity-0"
                              )}
                            />
                            {lead.channel === "whatsapp" && (
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="#25D366" className="mr-1.5 shrink-0">
                                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                              </svg>
                            )}
                            {lead.channel === "instagram" && (
                              <Instagram className="mr-1.5 h-4 w-4 shrink-0 text-pink-500" />
                            )}
                            {lead.channel === "messenger" && (
                              <MessengerIcon className="mr-1.5 text-blue-500" size={16} />
                            )}
                            <div className="min-w-0">
                              <div className="font-medium">{lead.name}</div>
                              <div className="text-xs text-muted-foreground">
                                {[lead.phone, lead.email].filter(Boolean).join(" · ") || "Sem contato"}
                              </div>
                            </div>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
          )}

          {/* Show locked lead info when rescheduling */}
          {isRescheduling && selectedLead && (
            <div className="space-y-2">
              <Label>Cliente</Label>
              <div className="p-3 bg-muted/50 rounded-lg border text-sm font-medium">
                {selectedLead.name}
              </div>
            </div>
          )}

          {/* Standalone Fields */}
          {!isRescheduling && leadMode === "standalone" && (
            <div className="space-y-4 p-3 bg-muted/50 rounded-lg border">
              <div className="space-y-2">
                <Label htmlFor="standalone-summary">
                  Título do compromisso <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="standalone-summary"
                  value={standaloneSummary}
                  onChange={(e) => setStandaloneSummary(e.target.value)}
                  placeholder="Ex: Reunião interna, Evento externo..."
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="standalone-name">Nome do participante</Label>
                  <Input
                    id="standalone-name"
                    value={standaloneAttendeeName}
                    onChange={(e) => setStandaloneAttendeeName(e.target.value)}
                    placeholder="Nome (opcional)"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="standalone-email">Email</Label>
                  <Input
                    id="standalone-email"
                    type="email"
                    value={standaloneAttendeeEmail}
                    onChange={(e) => setStandaloneAttendeeEmail(e.target.value)}
                    placeholder="email@exemplo.com"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Lead Editable Fields (for selected lead or new lead — hidden when rescheduling) */}
          {!isRescheduling && leadMode !== "standalone" && (leadMode === "create" || selectedLeadId) && (
            <div className="space-y-4 p-3 bg-muted/50 rounded-lg border">
              <div className="space-y-2">
                <Label htmlFor="lead-name">
                  Nome {leadMode === "create" && <span className="text-destructive">*</span>}
                </Label>
                <Input
                  id="lead-name"
                  value={leadName}
                  onChange={(e) => setLeadName(e.target.value)}
                  placeholder="Nome do cliente"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="lead-phone">Telefone</Label>
                  <Input
                    id="lead-phone"
                    value={leadPhone}
                    onChange={(e) => setLeadPhone(e.target.value)}
                    placeholder="(11) 99999-9999"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="lead-email">Email</Label>
                  <Input
                    id="lead-email"
                    type="email"
                    value={leadEmail}
                    onChange={(e) => setLeadEmail(e.target.value)}
                    placeholder="email@exemplo.com"
                  />
                </div>
              </div>
              {leadMode === "create" && showAssignSelf && (
                <label className="flex items-center gap-2 text-sm">
                  <Checkbox
                    checked={assignSelfLocked || assignSelf}
                    disabled={assignSelfLocked}
                    onCheckedChange={(c) => setAssignSelf(c === true)}
                  />
                  Eu serei o responsável por este contato
                </label>
              )}
            </div>
          )}

          {/* Date and Time */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Data *</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !selectedDate && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {selectedDate ? format(selectedDate, "dd/MM/yyyy") : "Selecionar"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={setSelectedDate}
                    locale={ptBR}
                    disabled={(date) => isBefore(date, startOfDay(new Date()))}
                    className="pointer-events-auto"
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div className="space-y-2">
              <Label>Horário *</Label>
              <Select value={selectedTime} onValueChange={setSelectedTime}>
                <SelectTrigger>
                  <SelectValue placeholder="Horário" />
                </SelectTrigger>
                <SelectContent>
                  {TIME_SLOTS.map((slot) => (
                    <SelectItem key={slot.value} value={slot.value}>
                      {slot.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Product/Service/Plan (optional) */}
          <div className="space-y-2">
            <Label>Produto / Serviço (opcional)</Label>
            <Select value={selectedServiceId || "none"} onValueChange={(v) => setSelectedServiceId(v === "none" ? "" : v)}>
              <SelectTrigger>
                <SelectValue placeholder={products.length > 0 ? "Selecionar item..." : "Nenhum produto cadastrado"} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Nenhum</SelectItem>
                {products.map((product) => (
                  <SelectItem key={product.id} value={product.id}>
                    {product.name}
                    {product.type && ` (${product.type === "service" ? "Serviço" : product.type === "plan" ? "Plano" : "Produto"})`}
                    {product.duration ? ` — ${product.duration}min` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Appointment Reason (when no service selected) */}
          {!selectedServiceId && (
            <div className="space-y-2">
              <Label>Motivo do agendamento <span className="text-destructive">*</span></Label>
              <Input
                value={appointmentReason}
                onChange={(e) => setAppointmentReason(e.target.value)}
                placeholder="Ex: Consulta inicial, Reunião de alinhamento..."
              />
            </div>
          )}

          {/* Duration */}
          <div className="space-y-2">
            <Label>Duração</Label>
            <Select value={duration.toString()} onValueChange={(v) => setDuration(Number(v))}>
              <SelectTrigger>
                <SelectValue placeholder="Duração" />
              </SelectTrigger>
              <SelectContent>
                {DURATION_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value.toString()}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {selectedServiceId && products.find((p) => p.id === selectedServiceId)?.duration && (
              <p className="text-xs text-muted-foreground">
                Duração definida pelo item selecionado
              </p>
            )}
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label>Observações</Label>
            <Textarea
              placeholder="Notas sobre o agendamento..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
            />
          </div>

          {/* Google Calendar Sync */}
          {isCheckingCalendarStatus ? (
            <div className="flex items-center gap-2 pt-2 text-xs text-muted-foreground">
              <Loader2 className="h-3 w-3 animate-spin" />
              Verificando conexão com Google Calendar…
            </div>
          ) : hasGoogleCalendar ? (
            <div className="space-y-3 pt-2">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="sync-google"
                  checked={syncGoogleCalendar}
                  onCheckedChange={(checked) => setSyncGoogleCalendar(checked === true)}
                />
                <Label htmlFor="sync-google" className="text-sm font-normal cursor-pointer">
                  Criar evento no Google Calendar
                </Label>
              </div>
              {syncGoogleCalendar && visibleSyncCalendars.length > 0 && (
                <div className="space-y-2">
                  <Label>Criar em qual agenda?</Label>
                  <Select
                    value={selectedSyncCalendarId || undefined}
                    onValueChange={setSelectedSyncCalendarId}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione a agenda" />
                    </SelectTrigger>
                    <SelectContent>
                      {visibleSyncCalendars.map((cal) => (
                        <SelectItem key={cal.id} value={cal.id}>
                          {cal.display_name || cal.calendar_id}
                          {cal.is_primary ? " (primária)" : ""}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          ) : null}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>
            Cancelar
          </Button>
          <Button onClick={handleSubmit} disabled={loading || isCheckingCalendarStatus}>
            {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {isRescheduling ? "Reagendar" : "Criar Agendamento"}
          </Button>
        </DialogFooter>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>

    {/* Pill flutuante — portal no body para ficar acima de qualquer rota */}
    {open && minimized && typeof document !== "undefined" && createPortal(
      <div className="fixed bottom-4 right-4 z-[100] flex items-center gap-1 pointer-events-auto">
        <button
          type="button"
          onClick={() => setMinimized(false)}
          className="flex items-center gap-2 rounded-full border bg-background pl-4 pr-3 py-2.5 text-sm font-medium shadow-lg hover:bg-accent transition-colors"
          aria-label="Retomar agendamento"
        >
          <CalendarIcon className="h-4 w-4 text-primary" />
          <span>
            {isRescheduling ? "Reagendar" : "Agendamento"}
            {selectedLead?.name ? ` · ${selectedLead.name}` : leadName ? ` · ${leadName}` : ""}
          </span>
          <span className="ml-1 inline-flex h-6 w-6 items-center justify-center rounded-full bg-primary/10 text-primary">
            <Maximize2 className="h-3.5 w-3.5" />
          </span>
        </button>
        <button
          type="button"
          onClick={() => onOpenChange(false)}
          className="inline-flex h-10 w-10 items-center justify-center rounded-full border bg-background shadow-lg hover:bg-accent transition-colors"
          aria-label="Descartar agendamento"
          title="Descartar"
        >
          <X className="h-4 w-4" />
        </button>
      </div>,
      document.body
    )}


    {/* Double booking confirmation */}
    <AlertDialog open={doubleBookingOpen} onOpenChange={(open) => { if (!open) setDoubleBookingOpen(false); }}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Agendamento Duplicado</AlertDialogTitle>
          <AlertDialogDescription>
            Este lead já possui um agendamento futuro em{" "}
            <strong>
              {existingAppointmentDate
                ? format(new Date(existingAppointmentDate), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })
                : ""}
            </strong>.
            <span className="block mt-2">Deseja criar outro agendamento mesmo assim?</span>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancelar</AlertDialogCancel>
          <AlertDialogAction
            onClick={() => {
              setDoubleBookingOpen(false);
              // Re-trigger submit bypassing the double-booking check
              handleSubmit();
            }}
          >
            Agendar mesmo assim
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    {showReconnect && (
      <GoogleCalendarDialog open={showReconnect} onOpenChange={setShowReconnect} />
    )}
    </>
  );
}
